import { useState, useEffect, useCallback } from 'react';
import { Loader2, Rocket, ListTodo, CheckSquare, ArrowRight, GitBranch } from 'lucide-react';
import { workshopApi } from '../../../services/api';
import { type LineageEvent, type LineageEntityType } from '../../../types';

const ENTITY_META: Record<LineageEntityType, { icon: typeof Rocket; color: string; label: string }> = {
  feature: { icon: Rocket,     color: 'text-primary-600 dark:text-primary-400', label: 'Feature' },
  item:    { icon: ListTodo,   color: 'text-amber-600 dark:text-amber-400',     label: 'Item' },
  uat:     { icon: CheckSquare, color: 'text-teal-600 dark:text-teal-400',      label: 'UAT' },
};

function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
}

export default function LineageView() {
  const [events, setEvents] = useState<LineageEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<LineageEntityType | ''>('');

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setEvents(await workshopApi.listLineage({
        limit: 200,
        entity_type: filter || undefined,
      }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load lineage');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 mb-3">
        <GitBranch size={14} className="text-primary-600 dark:text-primary-400" />
        <p className="text-xs text-gray-500 dark:text-gray-400 flex-1">
          Append-only audit log of every state change. Newest first. This is what powers a future changelog export.
        </p>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as LineageEntityType | '')}
          className="px-2 py-1 text-xs border border-gray-300 dark:border-surface-800 rounded bg-white dark:bg-surface-850 text-gray-900 dark:text-white"
        >
          <option value="">All entities</option>
          <option value="feature">Features only</option>
          <option value="item">Items only</option>
          <option value="uat">UAT cases only</option>
        </select>
      </div>

      {error && (
        <div className="p-2 mb-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 rounded text-xs text-red-700 dark:text-red-300">{error}</div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto border border-gray-200 dark:border-surface-800 rounded bg-white dark:bg-surface-900">
        {loading ? (
          <div className="p-4 text-center text-sm text-gray-500"><Loader2 size={14} className="animate-spin inline mr-1" />Loading…</div>
        ) : events.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500">No lineage events yet. Create something in Backlog or Features to start the trail.</div>
        ) : (
          events.map((e) => {
            const meta = ENTITY_META[e.entity_type];
            const Icon = meta.icon;
            return (
              <div key={e.id} className="px-3 py-2 border-b border-gray-100 dark:border-surface-800 flex items-start gap-2">
                <Icon size={14} className={`${meta.color} mt-0.5 flex-shrink-0`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1 text-xs text-gray-900 dark:text-white">
                    <span className="text-gray-500">{meta.label}:</span>
                    <span className="font-medium truncate">{e.entity_label ?? e.entity_id.slice(0, 8)}</span>
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 flex items-center gap-1 flex-wrap">
                    {e.from_state ? (
                      <>
                        <span className="font-mono px-1 rounded bg-gray-100 dark:bg-surface-850">{e.from_state}</span>
                        <ArrowRight size={10} className="text-gray-400" />
                      </>
                    ) : (
                      <span className="text-gray-500">created as</span>
                    )}
                    <span className="font-mono px-1 rounded bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300">{e.to_state}</span>
                    {e.note && <span className="text-gray-500 ml-1">— {e.note}</span>}
                  </div>
                </div>
                <div className="text-[10px] text-gray-400 text-right flex-shrink-0">
                  <div>{formatRelative(e.created_at)}</div>
                  {e.by_user_email && <div className="opacity-75 truncate max-w-[10rem]">{e.by_user_email}</div>}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
